import {
  flow,
  getParent,
  getRoot,
  type Instance,
  types,
} from 'mobx-state-tree';

import { ApplicationSource } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { fetchPreviousApplications } from 'src/graphql/fetchPreviousApplications.hirer.graphql';
import type { PageConfig } from 'src/libs/PageConfig';

import type { IStore } from '../Store';
import { STATE_ERROR, STATE_LOADED, stateType } from '../state';

import Attachment from './Attachment';
import type { IJobApplication } from './JobApplication';
import Notes from './Notes';
import ProspectEmailStore from './ProspectEmailStore';
import { Rating } from './Rating';
import { BUCKETS } from './types';
import { mapToBucket } from './utils/mapApiResponseToStore';
import { mapGraphAttachmentTypeToStore } from './utils/mapGraphAttachmentTypeToStore';

export const PositionSummary = types.model('PositionSummary', {
  id: types.optional(types.string, ''),
  title: types.optional(types.string, ''),
});

export interface SelectPreviousApplicationParams {
  selectedPreviousApplicationId: string;
  jobId: string;
  applicationCorrelationId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

export const Prospect = types
  .model('Prospect', {
    id: types.identifier,
    candidateId: types.string,
    // query.previousApplications returns jobCandidateId in the .id field.
    // the reason why I picked jobCandidateId over applicationCorrelationId is to keep the naming consistent with what's available in dbo.Prospect.
    jobCandidateId: types.string,
    mostRecentJobTitle: types.optional(types.string, ''),
    mostRecentCompanyName: types.optional(types.string, ''),
    mostRecentRoleMonths: types.maybeNull(types.number),
    appliedDate: types.optional(types.string, ''),
    bucket: types.optional(
      types.enumeration<BUCKETS>('Buckets', Object.values(BUCKETS)),
      BUCKETS.UNKNOWN,
    ),
    attachments: types.array(Attachment),
    rating: types.optional(Rating, {}),
    source: types.optional(types.number, 0),
    prospectEmailStore: types.optional(ProspectEmailStore, {}),
    notes: types.optional(Notes, {}),
    positionSummary: types.optional(PositionSummary, {}),
    seekCandidateId: types.maybeNull(types.string),
    jobApplicationId: types.string,
  })
  .actions((self) => {
    const loadEmailsAndNotes = flow(function* ({
      addCustomError,
    }: {
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      self.prospectEmailStore.loadEmails(addCustomError);
      self.notes.loadNotes({
        jobId: self.positionSummary.id,
        applicationCorrelationId: self.jobCandidateId,
        addCustomError,
      });
    });

    return { loadEmailsAndNotes };
  });

export type IProspect = Instance<typeof Prospect>;

const DEFAULT_PROSPECT_COUNT = 1;

const CandidateStore = types
  .model('CandidateStore', {
    prospectCount: types.optional(types.number, DEFAULT_PROSPECT_COUNT),
    previousApplications: types.array(Prospect),
    selectedPreviousApplicationId: types.maybeNull(types.string),
    state: stateType,
  })
  .views((self) => ({
    get rootStore() {
      return getRoot(self) as IStore;
    },

    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get previousApplicationCount() {
      return self.prospectCount - 1;
    },
  }))
  .views((self) => ({
    get selectedPreviousApplication() {
      return self.previousApplications.find(
        (prospect) => prospect.id === self.selectedPreviousApplicationId,
      );
    },
  }))
  .actions((self) => {
    const selectPreviousApplication = ({
      selectedPreviousApplicationId,
      jobId,
      applicationCorrelationId,
      addCustomError,
    }: SelectPreviousApplicationParams) => {
      self.selectedPreviousApplicationId = selectedPreviousApplicationId;
      if (self.selectedPreviousApplication) {
        self.selectedPreviousApplication.prospectEmailStore.loadEmails(
          addCustomError,
        );
        self.selectedPreviousApplication.notes.loadNotes({
          jobId,
          applicationCorrelationId,
          addCustomError,
        });
      }
    };

    const loadCandidate = flow(function* ({
      jobId,
      applicationCorrelationId,
      candidateEmail,
      autoSelectFirstOne,
      addCustomError,
    }: {
      jobId: number;
      applicationCorrelationId: string;
      candidateEmail: string;
      autoSelectFirstOne: boolean;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      if (self.state === STATE_LOADED) {
        return;
      }

      try {
        const data = (yield fetchPreviousApplications({
          input: {
            jobId,
            applicationCorrelationId,
            candidateEmail,
          },
        })) as Awaited<ReturnType<typeof fetchPreviousApplications>>;

        if (data.previousApplications.__typename === 'ResponseError') {
          throw new Error('Error fetching previous applications');
        }

        const previousApplications = data.previousApplications.applications.map(
          (application) => {
            if (application.attachmentsV2.__typename === 'ResponseError') {
              throw new Error(
                'Error fetching previous application attachments',
              );
            }

            if (application.job.__typename === 'ResponseError') {
              throw new Error('Error fetching previous application job');
            }

            const sourceMap: Record<ApplicationSource, number> = {
              [ApplicationSource.NATIVE]: 1,
              [ApplicationSource.UPLOADED]: 2,
            };

            const prospect = Prospect.create({
              id: `${application.adcentreProspectId}`,
              candidateId: `${application.adcentreCandidateId}`,
              jobCandidateId: `${application.id}`,
              mostRecentJobTitle: application.mostRecentJobTitle || undefined,
              mostRecentCompanyName:
                application.mostRecentCompanyName || undefined,
              mostRecentRoleMonths: application.mostRecentRoleMonths,
              appliedDate: application.appliedDateUtc,
              positionSummary: {
                id: application.job.result.id,
                title: application.job.result.title,
              },
              attachments:
                application.attachmentsV2.__typename ===
                'ApplicationAttachmentsV2ResponseSuccess'
                  ? application.attachmentsV2.result.map((attachment) => ({
                      type: mapGraphAttachmentTypeToStore(
                        attachment.attachmentType,
                      ),
                      filename: attachment.fileName,
                      attachmentId: attachment.attachmentId,
                    }))
                  : [],
              source:
                sourceMap[application.source] ||
                sourceMap[ApplicationSource.NATIVE],
              bucket: mapToBucket(application.statusFolder),
              seekCandidateId: application.candidateId,
              jobApplicationId: application.applicationId ?? '',
            });

            return prospect;
          },
        );
        self.previousApplications = previousApplications as any; // FIXME: we cannot put a JavaScript array into MobX array because of typing. This `as any` is required to make it work.

        if (self.previousApplications.length > 0 && autoSelectFirstOne) {
          selectPreviousApplication({
            jobId: self.previousApplications[0].positionSummary.id,
            applicationCorrelationId:
              self.previousApplications[0].jobCandidateId,
            selectedPreviousApplicationId: self.previousApplications[0].id,
            addCustomError,
          });
        }

        self.state = STATE_LOADED;
      } catch (error: any) {
        self.state = STATE_ERROR;
        addCustomError({
          error,
          context: {
            activity: 'LOAD_CANDIDATE',
            description: 'Error attempting: fetchPreviousApplications',
            applicationCorrelationId,
          },
        });
      }
    });

    const unselectPreviousApplication = () => {
      self.selectedPreviousApplicationId = null;
    };

    return {
      loadCandidate,
      selectPreviousApplication,
      unselectPreviousApplication,
    };
  });

export type ICandidateStore = Instance<typeof CandidateStore>;

export default CandidateStore;
